import type { GuideArticle } from './articleTypes';

export const articles3: GuideArticle[] = [
  {
    slug: 'basetao-sellers',
    tag: { en: 'Sellers', fr: 'Vendeurs', de: 'Verkäufer', es: 'Vendedores', pt: 'Vendedores' },
    image:
      'https://readdy.ai/api/search-image?query=A%20storefront%20concept%20with%20product%20cards%20and%20a%20rating%20badge%20arranged%20on%20a%20warm%20cream%20desk%20soft%20natural%20light%20sage%20green%20accents%20editorial%20still%20life%20photography%20high%20detail%20airy%20fresh%20aesthetic&width=800&height=600&seq=art-sellers-01&orientation=landscape',
    metaTitle: {
      en: 'Basetao Seller Guide: How to Find Trusted Sellers',
      fr: 'Guide des vendeurs Basetao : trouver des vendeurs fiables',
      de: 'Basetao Verkäufer-Guide: vertrauenswürdige Verkäufer finden',
      es: 'Guía de vendedores Basetao: encontrar vendedores fiables',
      pt: 'Guia de vendedores Basetao: encontrar vendedores confiáveis',
    },
    metaDescription: {
      en: 'Learn how to evaluate Basetao sellers using reviews, ratings, QC history and other signals to find trustworthy sources for your orders.',
      fr: "Apprenez à évaluer les vendeurs Basetao grâce aux avis, notes, historique QC et autres signaux pour trouver des sources fiables.",
      de: 'Erfahre, wie du Basetao-Verkäufer anhand von Bewertungen, Ratings, QC-Historie und anderen Signalen bewertest, um zuverlässige Quellen zu finden.',
      es: 'Aprende a evaluar vendedores de Basetao con reseñas, valoraciones, historial QC y otras señales para encontrar fuentes fiables.',
      pt: 'Aprenda a avaliar vendedores do Basetao com avaliações, notas, histórico de QC e outros sinais para encontrar fontes confiáveis.',
    },
    h1: {
      en: 'Basetao Seller Guide: How to Find Trusted Sellers',
      fr: 'Guide des vendeurs Basetao : trouver des vendeurs fiables',
      de: 'Basetao Verkäufer-Guide: vertrauenswürdige Verkäufer finden',
      es: 'Guía de vendedores Basetao: encontrar vendedores fiables',
      pt: 'Guia de vendedores Basetao: encontrar vendedores confiáveis',
    },
    eyebrow: {
      en: 'BASETAO SELLER GUIDE',
      fr: 'GUIDE DES VENDEURS BASETAO',
      de: 'BASETAO VERKÄUFER-GUIDE',
      es: 'GUÍA DE VENDEDORES BASETAO',
      pt: 'GUIA DE VENDEDORES BASETAO',
    },
    intro: {
      en: 'The seller you choose matters as much as the product itself. A reliable seller means better quality, accurate descriptions and fewer surprises. Here is how to tell the good ones apart.',
      fr: "Le vendeur choisi compte autant que le produit. Un vendeur fiable signifie une meilleure qualité, des descriptions exactes et moins de surprises. Voici comment les distinguer.",
      de: 'Der gewählte Verkäufer zählt genauso wie das Produkt. Ein zuverlässiger Verkäufer bedeutet bessere Qualität, genaue Beschreibungen und weniger Überraschungen.',
      es: 'El vendedor importa tanto como el producto. Un vendedor fiable significa mejor calidad, descripciones exactas y menos sorpresas.',
      pt: 'O vendedor escolhido importa tanto quanto o produto. Um vendedor confiável significa melhor qualidade, descrições precisas e menos surpresas.',
    },
    sections: [
      {
        heading: {
          en: 'Check Ratings and Reviews',
          fr: 'Vérifiez notes et avis',
          de: 'Bewertungen und Rezensionen prüfen',
          es: 'Revisa valoraciones y reseñas',
          pt: 'Verifique notas e avaliações',
        },
        body: {
          en: 'A high rating with many reviews is more meaningful than a perfect score with only a few. Read the recent reviews for patterns about quality, sizing and communication.',
          fr: "Une note élevée avec beaucoup d'avis compte plus qu'un score parfait avec peu d'avis. Lisez les avis récents pour repérer des tendances sur la qualité et les tailles.",
          de: 'Ein hohes Rating mit vielen Bewertungen ist aussagekräftiger als ein perfektes Ergebnis mit wenigen. Lies aktuelle Rezensionen zu Qualität und Größen.',
          es: 'Una valoración alta con muchas reseñas vale más que una perfecta con pocas. Lee las recientes para ver patrones de calidad y tallas.',
          pt: 'Uma nota alta com muitas avaliações vale mais que uma perfeita com poucas. Leia as recentes para ver padrões de qualidade e tamanhos.',
        },
      },
      {
        heading: {
          en: 'Look at QC History',
          fr: 'Consultez l’historique QC',
          de: 'Auf die QC-Historie achten',
          es: 'Mira el historial QC',
          pt: 'Veja o histórico de QC',
        },
        body: {
          en: 'Sellers with a clean QC history are more likely to ship what they advertise. A Basetao spreadsheet often highlights sellers with strong track records.',
          fr: "Les vendeurs avec un historique QC propre livrent plus souvent ce qu'ils annoncent. Un tableur Basetao met souvent en avant les bons profils.",
          de: 'Verkäufer mit sauberer QC-Historie liefern eher, was sie anbieten. Eine Basetao-Tabelle hebt oft Verkäufer mit starkem Ruf hervor.',
          es: 'Los vendedores con historial QC limpio suelen enviar lo que anuncian. Una hoja Basetao destaca a los de buen historial.',
          pt: 'Vendedores com histórico de QC limpo tendem a enviar o que anunciam. Uma planilha Basetao destaca vendedores com bom histórico.',
        },
      },
      {
        heading: {
          en: 'Verify Product Descriptions',
          fr: 'Vérifiez les descriptions produits',
          de: 'Produktbeschreibungen prüfen',
          es: 'Verifica las descripciones',
          pt: 'Verifique as descrições',
        },
        body: {
          en: 'Compare the listing details against the photos and your needs. Vague or inconsistent descriptions are a reason to look elsewhere.',
          fr: "Comparez les détails de l'annonce avec les photos et vos besoins. Des descriptions vagues ou incohérentes sont une raison de chercher ailleurs.",
          de: 'Vergleiche Angebotsdetails mit Fotos und deinen Bedürfnissen. Vage oder widersprüchliche Beschreibungen sind ein Grund, weiterzusuchen.',
          es: 'Compara los detalles del anuncio con las fotos. Descripciones vagas o inconsistentes son motivo para buscar en otro lugar.',
          pt: 'Compare os detalhes do anúncio com as fotos. Descrições vagas ou inconsistentes são motivo para procurar outro vendedor.',
        },
      },
      {
        heading: {
          en: 'Start With a Small Order',
          fr: 'Commencez par une petite commande',
          de: 'Mit einer kleinen Bestellung starten',
          es: 'Empieza con un pedido pequeño',
          pt: 'Comece com um pedido pequeno',
        },
        body: {
          en: 'If you are unsure about a new seller, test them with a small, low-risk order first. A successful small order builds confidence before you spend more.',
          fr: "En cas de doute sur un nouveau vendeur, testez-le avec une petite commande à faible risque. Une petite commande réussie renforce la confiance.",
          de: 'Teste einen neuen Verkäufer zunächst mit einer kleinen, risikoarmen Bestellung. Ein Erfolg stärkt das Vertrauen vor größeren Ausgaben.',
          es: 'Si dudas de un vendedor nuevo, pruébalo con un pedido pequeño. Un pedido exitoso genera confianza antes de gastar más.',
          pt: 'Se tiver dúvidas sobre um vendedor novo, teste com um pedido pequeno. Um pedido bem-sucedido gera confiança antes de gastar mais.',
        },
      },
    ],
    faqs: [
      {
        q: {
          en: 'Can I trust top-rated sellers?',
          fr: 'Puis-je faire confiance aux vendeurs les mieux notés ?',
          de: 'Kann ich top-bewerteten Verkäufern vertrauen?',
          es: '¿Puedo confiar en los vendedores mejor valorados?',
          pt: 'Posso confiar nos vendedores mais bem avaliados?',
        },
        a: {
          en: 'Top ratings are a strong signal but not a guarantee. Always combine them with QC photos and your own judgment.',
          fr: "Les bonnes notes sont un signal fort, pas une garantie. Combinez-les toujours avec les photos QC et votre propre jugement.",
          de: 'Top-Bewertungen sind ein starkes Signal, aber keine Garantie. Kombiniere sie immer mit QC-Fotos und eigenem Urteil.',
          es: 'Las buenas valoraciones son una señal fuerte, no una garantía. Combínalas con fotos QC y tu propio criterio.',
          pt: 'Boas notas são um sinal forte, não uma garantia. Combine com fotos de QC e seu próprio julgamento.',
        },
      },
      {
        q: {
          en: 'What is a good review count?',
          fr: 'Quel est un bon nombre d’avis ?',
          de: 'Was ist eine gute Bewertungsanzahl?',
          es: '¿Qué es un buen número de reseñas?',
          pt: 'O que é um bom número de avaliações?',
        },
        a: {
          en: 'There is no fixed number, but hundreds of reviews across a long period are more reassuring than a handful of recent ones.',
          fr: "Il n'y a pas de chiffre fixe, mais des centaines d'avis sur une longue période rassurent plus que quelques avis récents.",
          de: 'Es gibt keine feste Zahl, aber Hunderte Bewertungen über einen langen Zeitraum sind beruhigender als wenige aktuelle.',
          es: 'No hay cifra fija, pero cientos de reseñas durante un largo periodo tranquilizan más que unas pocas recientes.',
          pt: 'Não há número fixo, mas centenas de avaliações ao longo do tempo tranquilizam mais que algumas recentes.',
        },
      },
      {
        q: {
          en: 'Do spreadsheets list trusted sellers?',
          fr: 'Les tableurs listent-ils des vendeurs fiables ?',
          de: 'Listen Tabellen vertrauenswürdige Verkäufer auf?',
          es: '¿Las hojas listan vendedores fiables?',
          pt: 'As planilhas listam vendedores confiáveis?',
        },
        a: {
          en: 'Many do, often with notes on quality and reliability. Still verify independently before placing a large order.',
          fr: "Beaucoup le font, souvent avec des notes sur la qualité et la fiabilité. Vérifiez quand même par vous-même avant une grosse commande.",
          de: 'Viele tun das, oft mit Notizen zu Qualität und Zuverlässigkeit. Prüfe vor großen Bestellungen trotzdem selbst.',
          es: 'Muchas sí, con notas de calidad y fiabilidad. Aun así, verifica por tu cuenta antes de un pedido grande.',
          pt: 'Muitas listam, com notas de qualidade e confiabilidade. Ainda assim, verifique por conta própria antes de um pedido grande.',
        },
      },
    ],
  },
  {
    slug: 'basetao-products',
    tag: { en: 'Products', fr: 'Produits', de: 'Produkte', es: 'Productos', pt: 'Produtos' },
    image:
      'https://readdy.ai/api/search-image?query=A%20grid%20of%20organized%20product%20thumbnail%20cards%20on%20a%20warm%20cream%20desk%20with%20a%20search%20icon%20and%20notebook%20soft%20natural%20light%20sage%20green%20accents%20editorial%20still%20life%20photography%20high%20detail%20airy%20fresh%20aesthetic&width=800&height=600&seq=art-products-01&orientation=landscape',
    metaTitle: {
      en: 'Basetao Product Search Guide: Find Items Faster',
      fr: 'Guide de recherche de produits Basetao : trouver plus vite',
      de: 'Basetao Produktsuche: Artikel schneller finden',
      es: 'Guía de búsqueda de productos Basetao: encuentra más rápido',
      pt: 'Guia de busca de produtos Basetao: encontre mais rápido',
    },
    metaDescription: {
      en: 'Learn effective ways to search and filter Basetao products across categories so you can discover the items you actually want.',
      fr: "Apprenez à rechercher et filtrer efficacement les produits Basetao par catégorie pour découvrir les articles qui vous intéressent.",
      de: 'Erfahre, wie du Basetao-Produkte effektiv über Kategorien suchst und filterst, um die gewünschten Artikel zu finden.',
      es: 'Aprende a buscar y filtrar productos de Basetao por categoría para descubrir los artículos que quieres.',
      pt: 'Aprenda a buscar e filtrar produtos do Basetao por categoria para descobrir os itens que você quer.',
    },
    h1: {
      en: 'Basetao Product Search Guide: Find Items Faster',
      fr: 'Guide de recherche de produits Basetao',
      de: 'Basetao Produktsuche: Artikel schneller finden',
      es: 'Guía de búsqueda de productos Basetao',
      pt: 'Guia de busca de produtos Basetao',
    },
    eyebrow: {
      en: 'BASETAO PRODUCT GUIDE',
      fr: 'GUIDE PRODUITS BASETAO',
      de: 'BASETAO PRODUKT-GUIDE',
      es: 'GUÍA DE PRODUCTOS BASETAO',
      pt: 'GUIA DE PRODUTOS BASETAO',
    },
    intro: {
      en: 'Finding the right product is easier when you know how to search, filter and organize results. This guide shares practical ways to narrow down a huge catalog into items that match your needs.',
      fr: "Trouver le bon produit est plus simple quand on sait chercher, filtrer et organiser. Ce guide partage des méthodes pour réduire un vaste catalogue aux articles qui vous correspondent.",
      de: 'Das richtige Produkt zu finden, fällt leichter, wenn man Suchen, Filtern und Organisieren kennt. Dieser Guide zeigt Wege, einen großen Katalog auf passende Artikel einzugrenzen.',
      es: 'Encontrar el producto correcto es más fácil si sabes buscar, filtrar y organizar. Esta guía comparte formas de acotar un catálogo enorme a lo que necesitas.',
      pt: 'Encontrar o produto certo é mais fácil quando você sabe buscar, filtrar e organizar. Este guia mostra formas de reduzir um catálogo enorme aos itens que você precisa.',
    },
    sections: [
      {
        heading: {
          en: 'Start With a Clear Idea',
          fr: 'Partez d’une idée claire',
          de: 'Mit einer klaren Vorstellung starten',
          es: 'Empieza con una idea clara',
          pt: 'Comece com uma ideia clara',
        },
        body: {
          en: 'Before searching, define what you want: the category, your budget and any must-have features. A clear target makes filtering much faster.',
          fr: "Avant de chercher, définissez ce que vous voulez : catégorie, budget et caractéristiques indispensables. Une cible claire accélère le filtrage.",
          de: 'Definiere vor der Suche, was du willst: Kategorie, Budget und Muss-Merkmale. Ein klares Ziel beschleunigt das Filtern.',
          es: 'Antes de buscar, define qué quieres: categoría, presupuesto y características imprescindibles. Un objetivo claro acelera el filtrado.',
          pt: 'Antes de buscar, defina o que quer: categoria, orçamento e recursos essenciais. Um alvo claro acelera a filtragem.',
        },
      },
      {
        heading: {
          en: 'Use Categories and Filters',
          fr: 'Utilisez catégories et filtres',
          de: 'Kategorien und Filter nutzen',
          es: 'Usa categorías y filtros',
          pt: 'Use categorias e filtros',
        },
        body: {
          en: 'Browse by category such as shoes, hoodies, t-shirts or bags, then apply filters for price, size or color. This quickly removes items you do not want.',
          fr: "Parcourez par catégorie (chaussures, hoodies, t-shirts, sacs), puis filtrez par prix, taille ou couleur. Cela élimine vite les articles inutiles.",
          de: 'Stöbere nach Kategorie wie Schuhe, Hoodies, T-Shirts oder Taschen und filtere dann nach Preis, Größe oder Farbe. Das entfernt unerwünschte Artikel schnell.',
          es: 'Navega por categoría (zapatos, sudaderas, camisetas, bolsos) y filtra por precio, talla o color. Así eliminas rápido lo que no quieres.',
          pt: 'Navegue por categoria (tênis, moletons, camisetas, bolsas) e filtre por preço, tamanho ou cor. Isso remove rapidamente o que você não quer.',
        },
      },
      {
        heading: {
          en: 'Leverage a Spreadsheet',
          fr: 'Exploitez un tableur',
          de: 'Eine Tabelle nutzen',
          es: 'Aprovecha una hoja de cálculo',
          pt: 'Use uma planilha',
        },
        body: {
          en: 'A Basetao spreadsheet organizes products by category with prices, links and notes, so you can compare many options side by side without searching one by one.',
          fr: "Un tableur Basetao organise les produits par catégorie avec prix, liens et notes, pour comparer de nombreuses options sans chercher une par une.",
          de: 'Eine Basetao-Tabelle organisiert Produkte nach Kategorie mit Preisen, Links und Notizen, sodass du viele Optionen nebeneinander vergleichen kannst.',
          es: 'Una hoja Basetao organiza productos por categoría con precios, enlaces y notas para comparar muchas opciones sin buscar una a una.',
          pt: 'Uma planilha Basetao organiza produtos por categoria com preços, links e notas para comparar várias opções sem buscar uma a uma.',
        },
      },
      {
        heading: {
          en: 'Save and Track Your Finds',
          fr: 'Enregistrez et suivez vos trouvailles',
          de: 'Funde speichern und verfolgen',
          es: 'Guarda y sigue tus hallazgos',
          pt: 'Salve e acompanhe seus achados',
        },
        body: {
          en: 'Keep a shortlist of promising items with their links and prices. Prices change often, so revisiting your list before ordering helps you catch the best moment.',
          fr: "Gardez une liste des articles prometteurs avec liens et prix. Les prix changent souvent, revoir votre liste avant de commander aide à saisir le bon moment.",
          de: 'Führe eine Auswahlliste mit Links und Preisen. Preise ändern sich oft; die Liste vor der Bestellung zu prüfen, hilft, den besten Moment zu erwischen.',
          es: 'Mantén una lista con enlaces y precios. Los precios cambian; revisarla antes de pedir ayuda a encontrar el mejor momento.',
          pt: 'Mantenha uma lista com links e preços. Os preços mudam; revisá-la antes de pedir ajuda a pegar o melhor momento.',
        },
      },
    ],
    faqs: [
      {
        q: {
          en: 'Can I search by image?',
          fr: 'Puis-je rechercher par image ?',
          de: 'Kann ich nach Bild suchen?',
          es: '¿Puedo buscar por imagen?',
          pt: 'Posso buscar por imagem?',
        },
        a: {
          en: 'Some marketplaces support image search, which can help you find similar items from a photo you already have.',
          fr: "Certaines places de marché prennent en charge la recherche par image, utile pour trouver des articles similaires à partir d'une photo.",
          de: 'Manche Marktplätze unterstützen die Bildersuche, um ähnliche Artikel von einem vorhandenen Foto zu finden.',
          es: 'Algunos marketplaces permiten buscar por imagen para encontrar artículos similares desde una foto.',
          pt: 'Alguns marketplaces suportam busca por imagem para encontrar itens semelhantes a partir de uma foto.',
        },
      },
      {
        q: {
          en: 'How do I compare similar products?',
          fr: 'Comment comparer des produits similaires ?',
          de: 'Wie vergleiche ich ähnliche Produkte?',
          es: '¿Cómo comparo productos similares?',
          pt: 'Como comparo produtos semelhantes?',
        },
        a: {
          en: 'Put them side by side using a spreadsheet, comparing price, seller rating, QC history and notes before deciding.',
          fr: "Placez-les côte à côte dans un tableur en comparant prix, note vendeur, historique QC et notes avant de décider.",
          de: 'Stelle sie in einer Tabelle nebeneinander und vergleiche Preis, Verkäuferbewertung, QC-Historie und Notizen.',
          es: 'Colócalos lado a lado en una hoja comparando precio, valoración, historial QC y notas antes de decidir.',
          pt: 'Coloque-os lado a lado em uma planilha comparando preço, nota, histórico de QC e observações.',
        },
      },
      {
        q: {
          en: 'Are listed prices final?',
          fr: 'Les prix affichés sont-ils définitifs ?',
          de: 'Sind die gelisteten Preise endgültig?',
          es: '¿Los precios listados son finales?',
          pt: 'Os preços listados são finais?',
        },
        a: {
          en: 'Prices can change, and shipping or service fees are added on top. Treat listed prices as a guide, not a final total.',
          fr: "Les prix peuvent changer et des frais s'ajoutent. Considérez-les comme une indication, pas un total final.",
          de: 'Preise können sich ändern, und Versand- oder Servicegebühren kommen hinzu. Betrachte Listenpreise als Richtwert.',
          es: 'Los precios pueden cambiar y se añaden tarifas. Trátalos como guía, no como total final.',
          pt: 'Os preços podem mudar e taxas são adicionadas. Trate-os como referência, não como total final.',
        },
      },
    ],
  },
  {
    slug: 'basetao-comparisons',
    tag: { en: 'Comparisons', fr: 'Comparaisons', de: 'Vergleiche', es: 'Comparativas', pt: 'Comparações' },
    image:
      'https://readdy.ai/api/search-image?query=Two%20side%20by%20side%20product%20cards%20with%20check%20marks%20and%20a%20balance%20scale%20on%20a%20warm%20cream%20desk%20soft%20natural%20light%20sage%20green%20accents%20editorial%20still%20life%20photography%20high%20detail%20airy%20fresh%20aesthetic&width=800&height=600&seq=art-compare-01&orientation=landscape',
    metaTitle: {
      en: 'Basetao vs Superbuy, CSSBuy & Sugargoo: Buying Agent Comparison',
      fr: 'Basetao vs Superbuy, CSSBuy et Sugargoo : comparatif',
      de: 'Basetao vs Superbuy, CSSBuy und Sugargoo: Vergleich',
      es: 'Basetao vs Superbuy, CSSBuy y Sugargoo: comparativa',
      pt: 'Basetao vs Superbuy, CSSBuy e Sugargoo: comparação',
    },
    metaDescription: {
      en: 'Compare Basetao with Superbuy, CSSBuy and Sugargoo across shipping, fees, QC and usability to choose the best buying agent for you.',
      fr: "Comparez Basetao à Superbuy, CSSBuy et Sugargoo sur la livraison, les frais, le QC et la facilité d'utilisation pour bien choisir.",
      de: 'Vergleiche Basetao mit Superbuy, CSSBuy und Sugargoo bei Versand, Gebühren, QC und Bedienung, um den besten Agenten zu wählen.',
      es: 'Compara Basetao con Superbuy, CSSBuy y Sugargoo en envío, tarifas, QC y usabilidad para elegir el mejor agente.',
      pt: 'Compare o Basetao com Superbuy, CSSBuy e Sugargoo em envio, taxas, QC e usabilidade para escolher o melhor agente.',
    },
    h1: {
      en: 'Basetao vs Superbuy, CSSBuy & Sugargoo',
      fr: 'Basetao vs Superbuy, CSSBuy et Sugargoo',
      de: 'Basetao vs Superbuy, CSSBuy und Sugargoo',
      es: 'Basetao vs Superbuy, CSSBuy y Sugargoo',
      pt: 'Basetao vs Superbuy, CSSBuy e Sugargoo',
    },
    eyebrow: {
      en: 'BASETAO COMPARISON GUIDE',
      fr: 'GUIDE COMPARATIF BASETAO',
      de: 'BASETAO VERGLEICHS-GUIDE',
      es: 'GUÍA COMPARATIVA BASETAO',
      pt: 'GUIA DE COMPARAÇÃO BASETAO',
    },
    intro: {
      en: 'Several buying agents serve international shoppers, and each has slightly different strengths. This guide compares Basetao with three well-known alternatives so you can see how they stack up.',
      fr: "Plusieurs agents servent les acheteurs internationaux, chacun avec des atouts différents. Ce guide compare Basetao à trois alternatives connues pour voir comment ils se positionnent.",
      de: 'Mehrere Agenten bedienen internationale Käufer, jeder mit anderen Stärken. Dieser Guide vergleicht Basetao mit drei bekannten Alternativen.',
      es: 'Varios agentes atienden a compradores internacionales, cada uno con fortalezas distintas. Esta guía compara Basetao con tres alternativas conocidas.',
      pt: 'Vários agentes atendem compradores internacionais, cada um com pontos fortes diferentes. Este guia compara o Basetao com três alternativas conhecidas.',
    },
    sections: [
      {
        heading: {
          en: 'Basetao vs Superbuy',
          fr: 'Basetao vs Superbuy',
          de: 'Basetao vs Superbuy',
          es: 'Basetao vs Superbuy',
          pt: 'Basetao vs Superbuy',
        },
        body: {
          en: 'Superbuy is one of the largest and most established agents, with a polished interface. Basetao is often favored by buyers who value competitive fees and a straightforward, community-driven approach.',
          fr: "Superbuy est l'un des agents les plus grands et établis, avec une interface soignée. Basetao est souvent apprécié pour ses frais compétitifs et son approche simple.",
          de: 'Superbuy ist einer der größten und etabliertesten Agenten mit polierter Oberfläche. Basetao wird oft wegen wettbewerbsfähiger Gebühren und einfachem Ansatz geschätzt.',
          es: 'Superbuy es de los agentes más grandes y establecidos, con interfaz pulida. Basetao se valora por tarifas competitivas y un enfoque sencillo.',
          pt: 'Superbuy é um dos maiores e mais estabelecidos agentes, com interface refinada. O Basetao é valorizado por taxas competitivas e abordagem simples.',
        },
      },
      {
        heading: {
          en: 'Basetao vs CSSBuy',
          fr: 'Basetao vs CSSBuy',
          de: 'Basetao vs CSSBuy',
          es: 'Basetao vs CSSBuy',
          pt: 'Basetao vs CSSBuy',
        },
        body: {
          en: 'CSSBuy is known for a wide range of shipping lines and detailed options. Basetao competes with a focus on affordability and a large, active community that shares finds and spreadsheets.',
          fr: "CSSBuy est réputé pour sa large gamme de lignes de livraison. Basetao rivalise en misant sur l'abordabilité et une communauté active qui partage trouvailles et tableurs.",
          de: 'CSSBuy ist für viele Versandlinien und detaillierte Optionen bekannt. Basetao punktet mit Erschwinglichkeit und einer aktiven Community, die Funde und Tabellen teilt.',
          es: 'CSSBuy destaca por su amplia variedad de líneas de envío. Basetao compite por asequibilidad y una comunidad activa que comparte hallazgos y hojas.',
          pt: 'CSSBuy é conhecido por muitas linhas de envio. O Basetao compete com foco em acessibilidade e uma comunidade ativa que compartilha achados e planilhas.',
        },
      },
      {
        heading: {
          en: 'Basetao vs Sugargoo',
          fr: 'Basetao vs Sugargoo',
          de: 'Basetao vs Sugargoo',
          es: 'Basetao vs Sugargoo',
          pt: 'Basetao vs Sugargoo',
        },
        body: {
          en: 'Sugargoo emphasizes user-friendly tools and promotions. Basetao stands out for buyers who prioritize competitive pricing and easy access to community-curated product resources.',
          fr: "Sugargoo met l'accent sur des outils simples et des promotions. Basetao se distingue pour ceux qui privilégient des prix compétitifs et l'accès aux ressources communautaires.",
          de: 'Sugargoo setzt auf benutzerfreundliche Tools und Aktionen. Basetao hebt sich für Käufer ab, die wettbewerbsfähige Preise und kuratierte Produktressourcen schätzen.',
          es: 'Sugargoo enfatiza herramientas fáciles y promociones. Basetao destaca para quienes priorizan precios competitivos y recursos curados por la comunidad.',
          pt: 'Sugargoo destaca ferramentas fáceis e promoções. O Basetao se sobressai para quem prioriza preços competitivos e recursos curados pela comunidade.',
        },
      },
      {
        heading: {
          en: 'How to Choose',
          fr: 'Comment choisir',
          de: 'So wählst du',
          es: 'Cómo elegir',
          pt: 'Como escolher',
        },
        body: {
          en: 'The best agent depends on your priorities. Compare fees, shipping options, QC quality and interface before you commit, and start small with whichever service feels right.',
          fr: "Le meilleur agent dépend de vos priorités. Comparez frais, options de livraison, qualité QC et interface avant de vous engager, et commencez petit.",
          de: 'Der beste Agent hängt von deinen Prioritäten ab. Vergleiche Gebühren, Versandoptionen, QC-Qualität und Oberfläche, und starte klein.',
          es: 'El mejor agente depende de tus prioridades. Compara tarifas, opciones de envío, calidad QC e interfaz antes de comprometerte, y empieza con poco.',
          pt: 'O melhor agente depende das suas prioridades. Compare taxas, opções de envio, qualidade de QC e interface, e comece pequeno.',
        },
      },
    ],
    faqs: [
      {
        q: {
          en: 'Which agent is the cheapest?',
          fr: 'Quel agent est le moins cher ?',
          de: 'Welcher Agent ist am günstigsten?',
          es: '¿Qué agente es el más barato?',
          pt: 'Qual agente é o mais barato?',
        },
        a: {
          en: 'Costs vary by order, shipping line and fees. The cheapest option depends on your specific items and destination.',
          fr: "Les coûts varient selon la commande, la ligne et les frais. L'option la moins chère dépend de vos articles et de la destination.",
          de: 'Die Kosten variieren je nach Bestellung, Linie und Gebühren. Die günstigste Option hängt von deinen Artikeln und dem Ziel ab.',
          es: 'Los costes varían según pedido, línea y tarifas. La opción más barata depende de tus artículos y destino.',
          pt: 'Os custos variam por pedido, linha e taxas. A opção mais barata depende dos seus itens e destino.',
        },
      },
      {
        q: {
          en: 'Do they all offer QC photos?',
          fr: 'Proposent-ils tous des photos QC ?',
          de: 'Bieten alle QC-Fotos an?',
          es: '¿Todos ofrecen fotos QC?',
          pt: 'Todos oferecem fotos de QC?',
        },
        a: {
          en: 'Most reputable agents offer inspection photos, but the quality and cost can differ. Confirm the policy for the service you choose.',
          fr: "La plupart des agents sérieux offrent des photos d'inspection, mais la qualité et le coût diffèrent. Confirmez la politique du service choisi.",
          de: 'Die meisten seriösen Agenten bieten Inspektionsfotos, Qualität und Kosten unterscheiden sich aber. Bestätige die Richtlinie.',
          es: 'La mayoría de agentes serios ofrecen fotos de inspección, pero la calidad y el coste difieren. Confirma la política.',
          pt: 'A maioria dos agentes sérios oferece fotos de inspeção, mas qualidade e custo diferem. Confirme a política.',
        },
      },
      {
        q: {
          en: 'Can I switch agents easily?',
          fr: 'Puis-je changer d’agent facilement ?',
          de: 'Kann ich den Agenten leicht wechseln?',
          es: '¿Puedo cambiar de agente fácilmente?',
          pt: 'Posso trocar de agente facilmente?',
        },
        a: {
          en: 'You can try different services, but you would need to create separate accounts and manage orders on each platform independently.',
          fr: "Vous pouvez essayer différents services, mais il faudra créer des comptes séparés et gérer les commandes indépendamment.",
          de: 'Du kannst verschiedene Dienste testen, müsstest aber separate Konten anlegen und Bestellungen unabhängig verwalten.',
          es: 'Puedes probar varios, pero necesitarás cuentas separadas y gestionar pedidos de forma independiente.',
          pt: 'Você pode testar serviços diferentes, mas precisará de contas separadas e gerenciar pedidos de forma independente.',
        },
      },
    ],
  },
];